/**
 * Chiptune SFX — synthesized with WebAudio, zero audio assets.
 *
 * Subscribes to the combat bus events and plays tiny retro blips: square-wave
 * hits, noise whooshes, coin chirps, rarity-scaled drop arpeggios, level-up
 * fanfares. Everything is generated at play time from oscillators + one shared
 * noise buffer, so nothing is loaded and the bundle stays lean.
 *
 * The AudioContext starts suspended by browser policy; we resume it on the
 * first user gesture. A mute toggle (🔊/🔇 in the game toolbars) persists in
 * localStorage. Volumes are deliberately low — this plays every 1.8s round in
 * a window people keep open while working.
 *
 * @module ui/sfx
 */

import { bus } from '../core/events.js';

const PREF_KEY = 'pocketdungeon.sfx'; // '0' = muted; anything else = on

let ctx = null;      // AudioContext, created lazily on first gesture
let master = null;   // master GainNode
let noiseBuf = null; // shared 1s white-noise buffer
let muted = false;

try { muted = localStorage.getItem(PREF_KEY) === '0'; } catch { /* ignore */ }

function ensureCtx() {
  if (ctx) return ctx;
  const AC = window.AudioContext ?? window.webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  master = ctx.createGain();
  master.gain.value = muted ? 0 : 1;
  master.connect(ctx.destination);
  noiseBuf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const d = noiseBuf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return ctx;
}

/** Resume the context on the first user gesture (autoplay policy). */
function unlock() {
  if (!ensureCtx()) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
}

/**
 * One synthesized tone: oscillator with an exponential decay envelope and an
 * optional pitch slide. All SFX are built from a few of these.
 * @param {object} o
 * @param {number} o.freq start frequency (Hz)
 * @param {number} [o.slide] end frequency (Hz) — glides over the duration
 * @param {number} [o.dur=0.08] seconds
 * @param {string} [o.type='square'] oscillator type
 * @param {number} [o.vol=0.08] peak gain
 * @param {number} [o.at=0] start offset in seconds from now
 */
function tone({ freq, slide, dur = 0.08, type = 'square', vol = 0.08, at = 0 }) {
  if (!ctx || muted || ctx.state !== 'running') return;
  const t0 = ctx.currentTime + at;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (slide) osc.frequency.exponentialRampToValueAtTime(Math.max(30, slide), t0 + dur);
  g.gain.setValueAtTime(vol, t0);
  g.gain.exponentialRampToValueAtTime(0.0008, t0 + dur);
  osc.connect(g).connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

/**
 * Filtered noise burst — impacts, whooshes and rumbles.
 * @param {object} o
 * @param {number} [o.dur=0.1]
 * @param {number} [o.vol=0.06]
 * @param {number} [o.freq=1200] band-pass center
 * @param {number} [o.at=0]
 */
function noise({ dur = 0.1, vol = 0.06, freq = 1200, at = 0 } = {}) {
  if (!ctx || muted || ctx.state !== 'running') return;
  const t0 = ctx.currentTime + at;
  const src = ctx.createBufferSource();
  src.buffer = noiseBuf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = freq;
  bp.Q.value = 0.9;
  const g = ctx.createGain();
  g.gain.setValueAtTime(vol, t0);
  g.gain.exponentialRampToValueAtTime(0.0008, t0 + dur);
  src.connect(bp).connect(g).connect(master);
  src.start(t0, Math.random());
  src.stop(t0 + dur + 0.02);
}

// ---------------------------------------------------------------------------
// Sound vocabulary
// ---------------------------------------------------------------------------

const RARITY_NOTES = {
  common:    [440],
  uncommon:  [440, 554],
  rare:      [440, 554, 659],
  epic:      [554, 659, 880],
  legendary: [659, 880, 1109, 1319],
  mythic:    [880, 1109, 1319, 1760],
};

const SFX = {
  heroHit(crit) {
    if (crit) {
      tone({ freq: 720, slide: 1140, dur: 0.1, vol: 0.09 });
      tone({ freq: 1440, dur: 0.06, vol: 0.05, at: 0.04, type: 'triangle' });
    } else {
      tone({ freq: 520 + Math.random() * 60, slide: 300, dur: 0.06, vol: 0.055 });
    }
    noise({ dur: 0.05, vol: 0.03, freq: 2400 });
  },
  monsterHit() {
    tone({ freq: 160, slide: 90, dur: 0.09, vol: 0.06, type: 'triangle' });
    noise({ dur: 0.07, vol: 0.035, freq: 500 });
  },
  dodge() {
    noise({ dur: 0.12, vol: 0.045, freq: 3200 });
  },
  kill(isBoss) {
    tone({ freq: 300, slide: 120, dur: 0.12, vol: 0.05, type: 'sawtooth' });
    // coin chirp
    tone({ freq: 1568, dur: 0.05, vol: 0.05, at: 0.08, type: 'square' });
    tone({ freq: 2093, dur: 0.09, vol: 0.05, at: 0.13, type: 'square' });
    if (isBoss) {
      tone({ freq: 392, dur: 0.16, vol: 0.07, at: 0.2, type: 'sawtooth' });
      tone({ freq: 523, dur: 0.16, vol: 0.07, at: 0.34, type: 'sawtooth' });
      tone({ freq: 784, dur: 0.3, vol: 0.08, at: 0.48, type: 'sawtooth' });
    }
  },
  drop(rarity) {
    const notes = RARITY_NOTES[rarity] ?? RARITY_NOTES.common;
    notes.forEach((f, i) => tone({ freq: f, dur: 0.09, vol: 0.06, at: i * 0.07, type: 'triangle' }));
  },
  food() {
    tone({ freq: 660, slide: 990, dur: 0.07, vol: 0.045, type: 'sine' });
  },
  levelUp() {
    [523, 659, 784, 1047].forEach((f, i) => tone({ freq: f, dur: 0.12, vol: 0.075, at: i * 0.09, type: 'square' }));
  },
  heroDown() {
    [392, 311, 233, 155].forEach((f, i) => tone({ freq: f, dur: 0.16, vol: 0.07, at: i * 0.12, type: 'sawtooth' }));
    noise({ dur: 0.3, vol: 0.04, freq: 300, at: 0.1 });
  },
  bossSpawn() {
    tone({ freq: 98, dur: 0.3, vol: 0.09, type: 'sawtooth' });
    tone({ freq: 92, dur: 0.34, vol: 0.09, at: 0.3, type: 'sawtooth' });
    noise({ dur: 0.5, vol: 0.04, freq: 200 });
  },
  ultimate() {
    tone({ freq: 220, slide: 880, dur: 0.3, vol: 0.07, type: 'sawtooth' });
    noise({ dur: 0.25, vol: 0.05, freq: 1000, at: 0.05 });
  },
  revive() {
    [523, 784, 1047].forEach((f, i) => tone({ freq: f, dur: 0.1, vol: 0.06, at: i * 0.08, type: 'triangle' }));
  },
};

// ---------------------------------------------------------------------------
// Wiring
// ---------------------------------------------------------------------------

/** Per-event-type minimum interval so a busy round never machine-guns. */
const THROTTLE_MS = { heroHit: 140, monsterHit: 160, dodge: 250, kill: 200, food: 400 };
const lastAt = {};

function throttled(kind) {
  const min = THROTTLE_MS[kind] ?? 0;
  const now = performance.now();
  if (now - (lastAt[kind] ?? 0) < min) return true;
  lastAt[kind] = now;
  return false;
}

export function isMuted() { return muted; }

export function setMuted(next) {
  muted = !!next;
  try { localStorage.setItem(PREF_KEY, muted ? '0' : '1'); } catch { /* ignore */ }
  if (master) master.gain.value = muted ? 0 : 1;
  bus.emit('sfx:muted', muted);
}

/** Hook every combat event to its sound. Call once at boot. */
export function initSfx() {
  // Unlock audio on the first real user interaction anywhere.
  const gestures = ['pointerdown', 'keydown'];
  const onGesture = () => {
    unlock();
    gestures.forEach((g) => document.removeEventListener(g, onGesture));
  };
  gestures.forEach((g) => document.addEventListener(g, onGesture));

  bus.on('combat:hero_attack', (e) => { if (!throttled('heroHit')) SFX.heroHit(e.crit); });
  bus.on('combat:monster_attack', () => { if (!throttled('monsterHit')) SFX.monsterHit(); });
  bus.on('combat:hero_dodge', () => { if (!throttled('dodge')) SFX.dodge(); });
  bus.on('combat:kill', (e) => { if (!throttled('kill')) SFX.kill(e.isBoss); });
  bus.on('combat:drop_equipment', (e) => SFX.drop(e.item?.rarity));
  bus.on('combat:drop_revive', () => SFX.drop('legendary'));
  bus.on('combat:drop_protection', () => SFX.drop('epic'));
  bus.on('combat:food_eat', () => { if (!throttled('food')) SFX.food(); });
  bus.on('combat:level_up', () => SFX.levelUp());
  bus.on('combat:hero_down', () => SFX.heroDown());
  bus.on('combat:hero_revive', () => SFX.revive());
  bus.on('combat:hero_revived_kit', () => SFX.revive());
  bus.on('combat:spawn', (e) => { if (e.monsters?.[0]?.isBoss) SFX.bossSpawn(); });
  bus.on('fx:ultimate', () => SFX.ultimate());
}
